import classes from "./Overview.module.css";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import MusicProjectModal from "../CreativeEditing/MusicProjectModal";
import { ServiceModal } from "../CreativeEditing/ServiceModal";

export const Overview = ({ creative }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [numberOfJobsRendering, setNumberOfJobsRendering] = useState(3);
  const [overviewWidth, setOverviewWidth] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedMusicProject, setSelectedMusicProject] = useState(null);
  const [isMusicModalOpen, setIsMusicModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleResize = () => {
    if (window.innerWidth < 1400) {
      setNumberOfJobsRendering(2);
    } else {
      setNumberOfJobsRendering(3);
    }

    if (window.innerWidth <= 1138 && window.innerWidth >= 743) {
      const computed = Math.max(0, Math.floor(window.innerWidth - 398));
      setOverviewWidth(computed);
    } else if (window.innerWidth <= 743) {
      setOverviewWidth(window.innerWidth);
    } else {
      setOverviewWidth(null);
    }
  }

  useEffect(() => {
    handleResize();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper to get the display URL for the first project image (handles YouTube/Vimeo links and uploaded videos with thumbnails)
  const getProjectThumbnailUrl = (project) => {
    if (!project) return null;
    
    const firstImage = project.images?.[0];
    const firstProcessedImage = project.processedImages?.[0];
    
    // Check if first image is an object (ProjectImage) and whether it's a link or video file
    if (firstImage && typeof firstImage === 'object') {
      const urlToCheck = firstImage.url || '';
      const isLink = firstImage.type === 'link' || 
        (urlToCheck && typeof urlToCheck === 'string' && 
         (urlToCheck.includes('youtube.com') || urlToCheck.includes('youtu.be') || urlToCheck.includes('vimeo.com')));

      const isVideoFile =
        urlToCheck && typeof urlToCheck === 'string' &&
        /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(urlToCheck);
      
      // For YouTube/Vimeo links, use thumbnailUrl from original image (old behavior)
      if (isLink && firstImage.thumbnailUrl) {
        return firstImage.thumbnailUrl;
      }

      // For uploaded videos with a thumbnail, prefer processedThumbnailUrl from processedImages
      if (isVideoFile) {
        if (firstProcessedImage && typeof firstProcessedImage === 'object' && firstProcessedImage.processedThumbnailUrl) {
          return firstProcessedImage.processedThumbnailUrl;
        }
        if (firstImage.thumbnailUrl) {
          return firstImage.thumbnailUrl;
        }
      }
    }
    
    // For regular images, use processedUrl or fallback
    if (typeof firstProcessedImage === 'string') {
      return firstProcessedImage;
    }
    
    return firstProcessedImage?.processedUrl || firstProcessedImage?.url || firstImage?.url || null;
  };

  const handleVisualProjectClick = (project) => {
    navigate(`/members/${id}/project/${project?.id}/overview`);
  };

  const handleMusicProjectClick = (project) => {
    setSelectedMusicProject(project);
    setIsMusicModalOpen(true);
  };

  const handleCloseMusicModal = () => {
    setIsMusicModalOpen(false);
    setSelectedMusicProject(null);
  };

  const handleServiceClick = (serviceId) => {
    const fullPackage = creative?.packages?.items?.find((pkg) => pkg.id === serviceId);
    if (fullPackage) {
      setSelectedService(fullPackage);
      setIsServiceModalOpen(true);
    }
  };

  const handleCloseServiceModal = () => {
    setIsServiceModalOpen(false);
    setSelectedService(null);
  };

  // Transform packages from creative data to match the expected structure
  const servicesArray = useMemo(() => {
    if (!creative?.packages?.items) return [];
    
    return creative.packages.items
      .filter(pkg => pkg.status === 'PUBLISHED' && pkg.isActive !== false)
      .map(pkg => {
        // Determine type: check if packageType or serviceType indicates "JobsInStudio"
        // Otherwise default to "Pakete"
        const packageTypeStr = String(pkg.packageType || '').toUpperCase();
        const serviceTypeStr = String(pkg.serviceType || '').toUpperCase();
        const isJobsInStudio = packageTypeStr.includes('JOBS') || 
                             packageTypeStr.includes('STUDIO') ||
                             serviceTypeStr.includes('JOBS') || 
                             serviceTypeStr.includes('STUDIO');
        
        return {
          id: pkg.id,
          type: isJobsInStudio ? 'JobsInStudio' : 'Pakete',
          name: pkg.name || '',
          price: pkg.pricePerHour || pkg.price || 0,
          description: pkg.description || '',
          image: pkg.processedPortfolioImages?.[0] || null,
          extras: pkg.deliverables?.[0] || pkg.includedServices?.[0] || ''
        };
      });
  }, [creative?.packages?.items]);

  // Separate services by type and prepare two columns
  const jobsInStudio = servicesArray.filter((s) => s.type === 'JobsInStudio');
  const pakete = servicesArray.filter((s) => s.type === 'Pakete');

  // Helper to avoid duplicates by id
  const byId = (arr) => new Set(arr.map((i) => i.id));

  // Start with up to 2 from each own type
  const initialPaketeSelection = pakete.slice(0, 2);

  // Fill deficits from the other type (only if the column has at least one own item)
  const paketeDeficit = Math.max(0, 2 - initialPaketeSelection.length);

  const usedByPakete = byId(initialPaketeSelection);

  // For JobsInStudio column, take fillers from Pakete excluding ones already picked for Pakete
  const paketeRemainingForJobs = pakete.filter((p) => !usedByPakete.has(p.id));

  // For Pakete column, take fillers from JobsInStudio excluding ones already used in Jobs column
  // Only render a column if that type has at least one original item
  const shouldRenderJobsColumn = jobsInStudio.length > 0;
  const shouldRenderPaketeColumn = pakete.length > 0;

  return (
      <>
      <div className={classes.overview} style={overviewWidth ? { width: overviewWidth } : undefined}>
        <div className={classes.uberDichContainer}>
          <h2 className="titleSemibold3">Über mich</h2>
          <p onClick={toggleExpand} className={`regularLarge ${!isExpanded ? classes.clamp3 : ''}`}>{creative?.description}</p>
      
          <button className="semiboldMiddle" onClick={toggleExpand}>
            {isExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
          </button>

          <div className={classes.servicesContentOverview}>
            {creative?.skills?.slice(0, 3).map((service, index) => (
                <div className={classes.serviceItemOverview} key={index}>
                    <span className="regularMiddle">{service}</span>
                </div>
            ))}
            {creative?.skills?.length > 3 && (
              <div className={classes.allViewButtonOverviewContainer}>
                <button onClick={toggleDropdown} className={`${classes.allViewButtonOverview} mediumLarge`}>+{creative?.skills?.length - 3}</button>
                {showDropdown && (
                    <div className={classes.dropdownContainerOverview}>
                        {creative?.skills?.slice(3).map((service, index) => (
                            <div className={classes.serviceItemOverview} key={index}>
                                <span className="regularMiddle">{service}</span>
                            </div>
                        ))}
                    </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={classes.horizontalLine}></div>
        <div className={classes.arbeitenContainer}>
          <div className={classes.arbeitenHeader}>
            <h2 className="titleSemibold3">Arbeiten</h2>
            <Link to={`/members/${id}/arbeiten`}>
              <span className="semboldXs">Alle anschauen</span> <span style={{color: "var(--grey-1)"}} className="regularXs">({creative?.projects?.items?.length})</span>
            </Link>
          </div> 
          <div className={classes.arbeitenContent}>
            {creative?.projects?.items?.slice(0, numberOfJobsRendering).map((project) => {
              if (creative?.creativeType === 'VISUAL') {
                const thumbnailUrl = getProjectThumbnailUrl(project);
                return (
                  <div 
                    onClick={() => handleVisualProjectClick(project)} 
                    className={classes.arbeitenItemContainer} 
                    key={project?.id}
                  >
                    <div className={classes.arbeitenItem}>
                      {thumbnailUrl && (
                        <img src={thumbnailUrl} alt={project.title} />
                      )}
                    </div>
                    <div className={classes.arbeitenItemText}>
                      <p style={{color: "var(--black)"}} className="regularXxs">{project.title}</p>
                      <p style={{color: "#8A8A91"}} className="slimRegularMiniText">{creative?.user?.firstName} {creative?.user?.lastName}</p>
                    </div>
                  </div>
                );
              } else {
                const audioData = project.audios?.[0];
                const coverImage = audioData?.processedCoverImage || audioData?.coverImage || project.images?.[0]?.url || project.processedImages?.[0]?.processedUrl;
                const album = audioData?.album || '';
                const durationMs = audioData?.durationMs;
                const duration = durationMs ? `${Math.floor(durationMs / 60000)}:${String(Math.floor((durationMs % 60000) / 1000)).padStart(2, '0')}` : '';
                return (
                  <div className={classes.musicianItemContainer} key={project?.id} onClick={() => handleMusicProjectClick(project)}>
                    <div className={classes.musicianImage}>
                      {coverImage && <img src={coverImage} alt={project.title} />}
                    </div>
                    <div className={classes.musicianTextContainer}>
                      <div className={classes.musicianTitleContainer}>
                        <p className="titleSemibold3">{project.title}</p>
                        {album && <p style={{color: "var(--dark-grey-3)"}} className="regularMiddle">{album}</p>}
                      </div>
                      {duration && (
                        <div className={classes.musicianDurationContainer}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
                            <path d="M7.63636 3.87119C8.12121 4.15066 8.12121 4.84934 7.63636 5.12881L1.09091 8.90164C0.60606 9.18111 0 8.83177 0 8.27283L0 0.727165C0 0.168226 0.606061 -0.18111 1.09091 0.0983596L7.63636 3.87119Z" fill="#1F1F1F"/>
                          </svg>
                          <p className="regularMiddle">{duration}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
            })}
          </div>
          <div className={classes.arbeitenContentTabletWrapper}>
            <div className={classes.arbeitenContentTablet}>
              {creative?.projects?.items?.slice(0, numberOfJobsRendering).map((project) => {
                if (creative?.creativeType === 'VISUAL') {
                  const thumbnailUrl = getProjectThumbnailUrl(project);
                  return (
                    <div 
                      onClick={() => handleVisualProjectClick(project)} 
                      className={classes.arbeitenItemContainer} 
                      key={project?.id}
                    >
                      <div className={classes.arbeitenItem}>
                        {thumbnailUrl && (
                          <img src={thumbnailUrl} alt={project?.title} />
                        )}
                      </div>
                      <div className={classes.arbeitenItemText}>
                        <p style={{color: "var(--black)"}} className="regularXxs">{project.title}</p>
                        <p style={{color: "#8A8A91"}} className="slimRegularMiniText">{creative?.user?.firstName} {creative?.user?.lastName}</p>
                      </div>
                    </div>
                  );
                } else {
                  const audioData = project.audios?.[0];
                  const coverImage = audioData?.processedCoverImage || audioData?.coverImage || project.images?.[0]?.url || project.processedImages?.[0]?.processedUrl;
                  const album = audioData?.album || '';
                  const durationMs = audioData?.durationMs;
                  const duration = durationMs ? `${Math.floor(durationMs / 60000)}:${String(Math.floor((durationMs % 60000) / 1000)).padStart(2, '0')}` : '';
                  return (
                    <div className={classes.musicianItemContainer} key={project?.id} onClick={() => handleMusicProjectClick(project)}>
                      <div className={classes.musicianImage}>
                        {coverImage && <img src={coverImage} alt={project?.title} />}
                      </div>
                      <div className={classes.musicianTextContainer}>
                        <div className={classes.musicianTitleContainer}>
                          <p className="titleSemibold3">{project.title}</p>
                          {album && <p style={{color: "var(--dark-grey-3)"}} className="regularMiddle">{album}</p>}
                        </div>
                        {duration && (
                          <div className={classes.musicianDurationContainer}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
                              <path d="M7.63636 3.87119C8.12121 4.15066 8.12121 4.84934 7.63636 5.12881L1.09091 8.90164C0.60606 9.18111 0 8.83177 0 8.27283L0 0.727165C0 0.168226 0.606061 -0.18111 1.09091 0.0983596L7.63636 3.87119Z" fill="#1F1F1F"/>
                            </svg>
                            <p className="regularMiddle">{duration}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
        <div className={classes.horizontalLine}></div>
        <div className={classes.servicesContainer}>
          <div className={classes.arbeitenHeader}>
            <h2 className="titleSemibold3">Services</h2>
            <Link to={`/members/${id}/services`} className="semboldXs">
              Alle anschauen <span style={{color: "var(--grey-1)"}} className="regularXs">({creative?.packages?.items?.length})</span>
            </Link>
          </div>
          <div className={classes.servicesColumns}>
            {shouldRenderJobsColumn && (
              <div className={classes.serviceColumn}>
                <h3 className="titleSemibold3">Arbeitszeit im Studio + Rohdateien </h3>
                <div className={classes.servicesContent}>
                  {jobsInStudio?.map((service) => (
                    <div onClick={() => handleServiceClick(service.id)} className={classes.serviceItem} key={`jobs-${service.id}`} style={{ cursor: "pointer" }}>
                      <div className={classes.serviceItemImage}>
                        {service?.image && <img src={service.image} alt={service.name} />}
                      </div>
                      <div className={classes.serviceItemText}>
                        <span className="semiboldMiddle">{service?.name}</span>
                        <span className="regularMiddle">{service?.price} € pro Stunde</span>
                        <span style={{color: "var(--dark-grey-4)" , marginBottom: "12px"}} className="regularXs">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                        <span className={`regularMiddle ${classes.serviceDescription}`}>{service?.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {shouldRenderPaketeColumn && (
              <div className={classes.serviceColumn}>
                <h3 className="titleSemibold3">Pakete</h3>
                <div className={classes.servicesContent}>
                  {pakete.map((service) => (
                    <div onClick={() => handleServiceClick(service.id)} className={classes.serviceItem} key={`pakete-${service.id}`} style={{ cursor: "pointer" }}>
                      <div className={classes.serviceItemImage}>
                        {service?.image && <img src={service.image} alt={service.name} />}
                      </div>
                      <div className={classes.serviceItemText}>
                        <span className="semiboldMiddle">{service.name}</span>
                        <span className="regularMiddle">{service.price} € pro Stunde</span>
                        <span style={{color: "var(--dark-grey-4)" , marginBottom: "12px"}} className="regularXs">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                        <span className={`regularMiddle ${classes.serviceDescription}`}>{service.description}</span>
                        {service.extras && (
                          <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                              <g clipPath="url(#clip0_15597_28902)">
                              <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                              <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                              <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                              <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                              </g>
                              <defs>
                              <clipPath id="clip0_15597_28902">
                              <rect width="21.8118" height="15.0954" fill="white"/>
                              </clipPath>
                              </defs>
                            </svg>
                            <span className="regularXss">{service.extras}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={classes.servicesContainerTablet}>
          <div className={classes.servicesHeaderTablet}>
            <h2 className="titleSemibold3">Services</h2>
            <Link to={`/members/${id}/services`} className="semboldXs">
              Alle anschauen <span style={{color: "var(--grey-1)"}} className="regularXs">({servicesArray.length})</span>
            </Link>
          </div>
          <div className={classes.servicesContentTabletWrapper}>
            <div className={classes.servicesContentTablet}> 
              {servicesArray.slice(0, 4).map((service) => (
                <div onClick={() => handleServiceClick(service.id)} className={classes.serviceItem} key={`service-${service.id}`} style={{ cursor: "pointer" }}>
                  <div className={classes.serviceItemImage}>
                    {service?.image && <img src={service.image} alt={service.name} />}
                  </div>
                  <div className={classes.serviceItemText}>
                    <span className="semiboldMiddle">{service.name}</span>
                    <span className="regularMiddle">{service.price} € pro Stunde</span>
                    <span 
                      style={{color: "var(--dark-grey-4)"}} 
                      className="slimRegularMiniText"
                    >
                        Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)
                    </span>
                    {service.extras && (
                      <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                            <g clipPath="url(#clip0_15597_28902)">
                            <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                            <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                            <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                            <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                            </g>
                            <defs>
                            <clipPath id="clip0_15597_28902">
                            <rect width="21.8118" height="15.0954" fill="white"/>
                            </clipPath>
                            </defs>
                          </svg>
                        <span className="regularXs">{service.extras}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {creative?.creativeType === 'MUSIC' && (
        <MusicProjectModal
          open={isMusicModalOpen}
          onClose={handleCloseMusicModal}
          project={selectedMusicProject}
          creative={creative}
          projectsArray={creative?.projects?.items}
          fromMemberPage={true}
        />
      )}
      {isServiceModalOpen && selectedService && (
        <ServiceModal
          open={isServiceModalOpen}
          onClose={handleCloseServiceModal}
          service={selectedService}
          creative={creative}
          servicesArray={creative?.packages?.items}
          hideEditButton={true}
        />
      )}
      </>
  );
};

export default Overview;